import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { 
  MessageSquare, 
  TrendingUp, 
  HelpCircle,
  CheckCircle2,
  XCircle,
  Edit2,
  Save,
  BarChart3,
  Users,
  Clock,
  Loader2,
  Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip 
} from 'recharts';

const ChatAnalyticsPage = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [autoFaqs, setAutoFaqs] = useState({ pending: [], active: [] });
  const [loading, setLoading] = useState(true);
  const [editingFaq, setEditingFaq] = useState(null);
  const [editAnswer, setEditAnswer] = useState('');

  useEffect(() => {
    fetchAnalytics();
    fetchAutoFaqs();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/support/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAutoFaqs = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/support/analytics/auto-faq`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoFaqs(response.data);
    } catch (error) {
      console.error('Failed to fetch auto FAQs:', error);
    }
  };

  const handleApproveFaq = async (questionNormalized, answer) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.put(`${API}/support/analytics/auto-faq/${encodeURIComponent(questionNormalized)}`, 
        { active: true, answer },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('FAQ approved and published');
      fetchAutoFaqs();
    } catch (error) {
      toast.error('Failed to approve FAQ');
    }
  };

  const handleRejectFaq = async (questionNormalized) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/support/analytics/auto-faq/${encodeURIComponent(questionNormalized)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('FAQ removed');
      fetchAutoFaqs();
    } catch (error) {
      toast.error('Failed to remove FAQ');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingFaq || !editAnswer.trim()) return;
    await handleApproveFaq(editingFaq.question_normalized, editAnswer);
    setEditingFaq(null);
    setEditAnswer('');
  };

  const categoryColors = {
    nutrition: '#22c55e',
    training: '#3b82f6',
    recovery: '#a855f7',
    account: '#f59e0b',
    challenges: '#ec4899',
    technical: '#ef4444',
    other: '#6b7280'
  };

  const pieData = analytics?.categories ? 
    Object.entries(analytics.categories)
      .filter(([_, count]) => count > 0)
      .map(([name, count]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: count,
        fill: categoryColors[name] || categoryColors.other
      })) : [];

  return (
    <DashboardLayout title="Chat Analytics">
      <div className="px-4 py-6 max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-heading font-bold text-2xl text-white uppercase tracking-wider">
            Support Chat Analytics
          </h1>
          <p className="text-aura-text-muted mt-1">
            Track support questions and manage auto-generated FAQs
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <MessageSquare className="w-6 h-6 text-aura-primary mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold text-white">
                {analytics?.total_chats || 0}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Total Chats</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-aura-success mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold text-white">
                {analytics?.week_chats || 0}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">This Week</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <HelpCircle className="w-6 h-6 text-yellow-400 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold text-white">
                {autoFaqs.pending?.length || 0}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Pending FAQs</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-6 h-6 text-green-400 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold text-white">
                {autoFaqs.active?.length || 0}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Active FAQs</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Category Distribution */}
          <Card className="bg-aura-surface border-white/10">
            <CardHeader>
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-aura-primary" />
                Question Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-aura-primary" />
                </div>
              ) : pieData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a1a2e', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-aura-text-muted">
                  No chat data yet
                </div>
              )}
              
              {/* Legend */}
              <div className="flex flex-wrap gap-3 mt-4 justify-center">
                {Object.entries(categoryColors).slice(0, -1).map(([cat, color]) => (
                  <div key={cat} className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                    <span className="text-xs text-aura-text-muted capitalize">{cat}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Trending Questions */}
          <Card className="bg-aura-surface border-white/10">
            <CardHeader>
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-aura-primary" />
                Trending Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.trending_questions?.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {analytics.trending_questions.slice(0, 10).map((q, i) => (
                    <div 
                      key={i} 
                      className="p-3 bg-white/5 rounded-lg flex items-start justify-between gap-3"
                    >
                      <p className="text-sm text-aura-text-muted line-clamp-2 flex-1">
                        {q.question_original || q.question_normalized}
                      </p>
                      <Badge className="bg-aura-primary/20 text-aura-primary flex-shrink-0">
                        {q.count}x
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-aura-text-muted">
                  No trending questions yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Auto-Generated FAQs */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              Auto-Generated FAQs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="pending">
              <TabsList className="bg-white/5 mb-4">
                <TabsTrigger value="pending" className="data-[state=active]:bg-aura-primary">
                  Pending ({autoFaqs.pending?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="active" className="data-[state=active]:bg-aura-primary">
                  Active ({autoFaqs.active?.length || 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pending">
                {autoFaqs.pending?.length > 0 ? (
                  <div className="space-y-3">
                    {autoFaqs.pending.map((faq, i) => (
                      <div 
                        key={i} 
                        className="p-4 bg-white/5 rounded-lg border border-white/10"
                      >
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div>
                            <p className="text-white font-medium">{faq.question}</p>
                            <p className="text-xs text-aura-text-muted mt-1">
                              Asked {faq.frequency} times
                            </p>
                          </div>
                          <Badge className="bg-yellow-500/20 text-yellow-400">
                            Pending
                          </Badge>
                        </div>

                        {editingFaq?.question_normalized === faq.question_normalized ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editAnswer}
                              onChange={(e) => setEditAnswer(e.target.value)}
                              placeholder="Write the answer..."
                              className="bg-aura-bg border-white/10 text-white min-h-[80px]"
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={handleSaveEdit}
                                className="bg-aura-success hover:bg-aura-success/90 text-white"
                              >
                                <Save className="w-4 h-4 mr-1" />
                                Save & Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingFaq(null)}
                                className="border-white/20 text-white"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                setEditingFaq(faq);
                                setEditAnswer(faq.answer || '');
                              }}
                              className="bg-aura-primary hover:bg-aura-primary-hover text-white"
                            >
                              <Edit2 className="w-4 h-4 mr-1" />
                              Edit & Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRejectFaq(faq.question_normalized)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-aura-text-muted">
                    <HelpCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No pending FAQs</p>
                    <p className="text-sm mt-1">Questions asked 10+ times will appear here</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="active">
                {autoFaqs.active?.length > 0 ? (
                  <div className="space-y-3">
                    {autoFaqs.active.map((faq, i) => (
                      <div 
                        key={i} 
                        className="p-4 bg-white/5 rounded-lg border border-green-500/20"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-white font-medium">{faq.question}</p>
                            <p className="text-sm text-aura-text-muted mt-2">{faq.answer}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRejectFaq(faq.question_normalized)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-aura-text-muted">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No active auto-FAQs yet</p>
                    <p className="text-sm mt-1">Approve pending FAQs to publish them</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ChatAnalyticsPage;
